import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createColumnHelper } from "@tanstack/react-table";

function HeadOfficeList({
  isAuthenticated,
  data,
  isLoading,
  leaseid,
  user,
  updateTableState,
  saveRecord,
  toggleHeadOfficeSearchDialog,
  controlType,
}) {
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(null);

  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const createId = useMemo(() => createElementIdFactory("head-office-list"), []);

  const col_led_head_office_id = columnHelper.accessor("led_head_office_id", {
    id: "led_head_office_id",
    header: "Head Office ID",
    cell: (e) => (
      <Link
        id={createId("link", "head-office", e.getValue(), e.row.id)}
        target="_blank"
        rel="noopener noreferrer"
        to={`../headoffices/${e.getValue()}`}
        state={{
          data: tableData.filter((a) => a.selected).map((a) => a.led_head_office_id),
        }}
      >
        {e.getValue()}
      </Link>
    ),
    size: 60,
    enableSorting: true,
  });

  const col_tenant_name = columnHelper.accessor("tenant_name", {
    id: "tenant_name",
    header: "Name",
    cell: (info) => info.getValue(),
    enableSorting: true,
  });

  const col_address_line_1 = columnHelper.accessor("address_line_1", {
    id: "address_line_1",
    header: "Address Line 1",
    cell: (info) => info.getValue(),
    enableSorting: true,
  });

  const col_address_line_2 = columnHelper.accessor("address_line_2", {
    id: "address_line_2",
    header: "Address Line 2",
    cell: (info) => info.getValue(),
    size: 120,
    enableSorting: true,
  });

  const col_suburb = columnHelper.accessor("suburb", {
    id: "suburb",
    header: "Suburb",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: true,
  });

  const col_state = columnHelper.accessor("state", {
    id: "state",
    header: "State",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: true,
  });

  const col_postcode = columnHelper.accessor("postcode", {
    id: "postcode",
    header: "Postcode",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: true,
  });

  const col_email = columnHelper.accessor("email", {
    id: "email",
    header: "Email",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: true,
  });

  const col_telephone = columnHelper.accessor("telephone", {
    id: "telephone",
    header: "Telephone",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: true,
  });

  const columns = [
    col_led_head_office_id,
    col_tenant_name,
    col_address_line_1,
    col_address_line_2,
    col_suburb,
    col_state,
    col_postcode,
    col_email,
    col_telephone,
  ];

  const columns2 = [
    col_led_head_office_id,
    col_tenant_name,
    col_address_line_1,
    col_address_line_2,
    col_suburb,
    col_state,
    col_postcode,
    col_email,
    col_telephone,
    columnHelper.accessor("head_office_id", {
      id: "head_office_id",
      header: "",
      cell: (info) => (
        <Button
          id={createId("button", "link-to-lease", info.row.original?.led_head_office_id || info.row.id)}
          type="button"
          size="sm"
          variant="primary"
          onClick={() => handleAddHeadOfficeLease(info.row.original)}
        >
          Link to Lease
        </Button>
      ),
      size: 60,
      enableSorting: false,
    }),
  ];

  const activeControlType = controlType === "table2" ? "table2" : "table";
  const activeColumns = activeControlType === "table2" ? columns2 : columns;

  async function handleAddHeadOfficeLease(headOffice) {
    const localData = [...tableData];

    const headOfficeLease = {
      led_head_office_id: headOffice?.led_head_office_id,
      lease_id: leaseid,
      created_by: user,
      last_edited_by: user,
      new_record: true,
    };

    localData.unshift(headOfficeLease);
    updateTableState(localData, "headofficeleases");

    const result = await saveRecord(headOfficeLease, "headOfficeLeases", "led_head_office_lease_id");

    if (!result.result) {
      setErrorMessage(result.error_message);
      setShowAlert(true);
    } else {
      toggleHeadOfficeSearchDialog();
    }
  }

  function renderHeadOffices() {
    return (
      <div id={createId("container", activeControlType)} className="componentRendering">
        <Modal id={createId("modal", "error")} show={showAlert} onHide={() => setShowAlert(!showAlert)} centered>
          <Modal.Header id={createId("modal", "error", "header")} closeButton>
            <Modal.Title id={createId("modal", "error", "title")}>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body id={createId("modal", "error", "body")}>
            <Alert id={createId("alert", "error")} variant="danger">{errorMessage}</Alert>
          </Modal.Body>
        </Modal>

        <Row id={createId("actions", "row")}>
          <Col id={createId("actions", "col")} className="text-right">
            <Link id={createId("link", "add")} to="/headoffices/new">
              <Button id={createId("button", "add")} variant="primary">Add</Button>
            </Link>
          </Col>
        </Row>

        {activeControlType === "table" && (
          <Row id={createId("record-count", "row")} className="mb-2">
            <Col id={createId("record-count", "col")}>
              <b id={createId("record-count", "text")}>
                {tableData.length} of {tableData[0]?.result_count || "0"} records returned.
              </b>
            </Col>
          </Row>
        )}

        <Row id={createId("table", "row")} className="show-Container">
          <Col id={createId("table", "col")} sm={12} md={12} lg={12}>
            {isLoading ? (
              <div id={createId("loading", "outer")} className="outer-div">
                <div id={createId("loading", "inner")} className="sweet-loading inner-div">
                  <PacmanLoader
                    id={createId("loading", "spinner")}
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color="#e03c31"
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : (
              <GenericTable
                columns={activeColumns}
                data={tableData}
                section="head-office-list"
                pageSize={PAGE_SIZES.headOfficeSearch}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }

  return <div id={createId("root")} className="HeadOfficeList">{isAuthenticated ? renderHeadOffices() : console.log("User not authenticated.")}</div>;
}

export default HeadOfficeList;
